/**
 * WildernessPanel — country-flagged PvP zones.
 *
 * Any Country Leader can flag one of their held regions as Wilderness from
 * the Country panel. Walking into a Wilderness region auto-flags you for
 * PvP: any player within ±10 combat levels in the same region can attack
 * you, and you them. Death drops 10% of your carried gold and up to 3 of
 * your lowest-value inventory items to the killer. On death you respawn
 * at your homeland.
 *
 * The panel has three shapes:
 *   1. Not in a Wilderness region -> browse the atlas of flagged regions
 *      and pick one to enter.
 *   2. In a Wilderness region     -> HP bar, current occupants (target
 *      list), attack button, bounty (if any), leave button.
 *   3. Recently killed             -> death splash + auto-refresh.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Flame, Skull, Swords, LogOut, MapPin, Heart, Coins, Target,
  ShieldAlert, Trophy, Clock, ChevronRight,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { flagUrl } from "@/lib/utils";
import { useOpenPlayer } from "@/components/game/PlayerCard";
import { ITEM_ICON, itemName } from "@/lib/gameData";

function StatChip({ icon: Icon, label, value, tone = "amber", testid }) {
  const tones = {
    amber:   "text-amber-300",
    red:     "text-red-300",
    green:   "text-emerald-300",
    stone:   "text-stone-300",
  };
  return (
    <div className="panel-inset px-3 py-2 flex items-center gap-2" data-testid={testid}>
      <Icon size={13} className={tones[tone] || tones.amber} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest font-numeric text-stone-500">{label}</div>
        <div className={"text-sm font-numeric " + (tones[tone] || tones.amber)}>{value}</div>
      </div>
    </div>
  );
}

function HpBar({ hp, max }) {
  const pct = max > 0 ? Math.round((hp / max) * 100) : 0;
  const tone = pct > 60 ? "bg-emerald-600" : pct > 30 ? "bg-amber-500" : "bg-red-700";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] font-numeric text-stone-400 mb-1">
        <span className="uppercase tracking-widest text-stone-500">Hitpoints</span>
        <span className="text-amber-300">{hp} / {max}</span>
      </div>
      <div className="h-2.5 rounded bg-stone-900 overflow-hidden border border-stone-800">
        <div className={"h-full transition-all " + tone} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RegionRow({ region, onEnter, busy }) {
  return (
    <div
      className="panel-inset p-3 flex items-center gap-3"
      data-testid={`wild-region-${region.region_id}`}
    >
      <div className="w-9 h-9 rounded border border-red-800 bg-red-950/30 flex items-center justify-center shrink-0">
        <Flame size={16} className="text-red-400" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-body text-sm text-stone-200 truncate">{region.region_name}</div>
        <div className="text-[11px] font-numeric text-stone-500 flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-sm"
            style={{ background: region.faction_color || "#c94a2c" }}
          />
          <span>{region.holder_name}</span>
          <span className="text-stone-700">·</span>
          <span>/{region.region_id}/</span>
        </div>
      </div>
      <button
        onClick={() => onEnter(region.region_id)}
        disabled={busy}
        className="text-xs font-body px-3 py-1.5 rounded border border-red-800 text-red-200 hover:bg-red-950/40 transition-colors disabled:opacity-50 flex items-center gap-1"
        data-testid={`wild-enter-${region.region_id}`}
      >
        Enter <ChevronRight size={12} />
      </button>
    </div>
  );
}

function TargetRow({ target, onAttack, busy, isBounty, onOpenProfile }) {
  const hpPct = Math.round((target.current_hp / (target.max_hp || 1)) * 100);
  return (
    <div
      className={
        "panel-inset p-3 flex items-center gap-3 " +
        (isBounty ? "ring-1 ring-amber-600/60 shadow-[0_0_18px_rgba(180,140,60,0.25)]" : "")
      }
      data-testid={`wild-target-${target.id}`}
    >
      <button
        onClick={onOpenProfile}
        className="w-10 h-10 rounded border-2 border-red-800 bg-red-950/40 flex items-center justify-center font-display text-red-300 shrink-0 hover:bg-red-950/60 transition-colors"
      >
        {(target.name || "?").slice(0, 2).toUpperCase()}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <button
            onClick={onOpenProfile}
            className="font-body text-sm text-stone-200 truncate hover:text-amber-200 transition-colors"
          >
            {target.name}
          </button>
          {target.iso_a2 && (
            <img
              src={flagUrl(target.iso_a2, 16)}
              alt=""
              className="w-3.5 h-2.5 object-cover rounded-[1px]"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          {isBounty && (
            <span className="text-[9px] font-numeric uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-950/60 text-amber-300 border border-amber-800">
              Bounty
            </span>
          )}
        </div>
        <div className="text-[11px] font-numeric text-stone-500">
          Combat Lv. <span className="text-amber-300">{target.combat_level}</span>
        </div>
        <div className="mt-1 h-1.5 rounded bg-stone-900 overflow-hidden">
          <div
            className={"h-full " + (hpPct > 60 ? "bg-emerald-600" : hpPct > 30 ? "bg-amber-500" : "bg-red-700")}
            style={{ width: `${hpPct}%` }}
          />
        </div>
      </div>
      <button
        onClick={() => onAttack(target.id)}
        disabled={busy}
        className="text-xs font-body px-3 py-1.5 rounded bg-red-800 hover:bg-red-700 text-stone-100 disabled:bg-stone-900 disabled:text-stone-500 transition-colors flex items-center gap-1"
        data-testid={`wild-attack-${target.id}`}
      >
        <Swords size={12} /> Strike
      </button>
    </div>
  );
}

function KillFeedRow({ k, meId }) {
  const iKilled  = k.killer_id === meId;
  const iDied    = k.victim_id === meId;
  const tone = iKilled ? "text-emerald-300" : iDied ? "text-red-300" : "text-stone-300";
  return (
    <div className="flex items-center gap-2 text-xs font-body py-1" data-testid={`wild-kill-${k.id}`}>
      <Skull size={11} className={tone} />
      <span className={"truncate " + tone}>
        <span className="font-numeric">{k.killer_name}</span>
        <span className="text-stone-600"> felled </span>
        <span className="font-numeric">{k.victim_name}</span>
      </span>
      <span className="ml-auto text-[10px] font-numeric text-amber-400">
        +{(k.gold_taken || 0).toLocaleString()} gp
      </span>
    </div>
  );
}

function BountyCard({ bounty }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const remMs = Math.max(0, new Date(bounty.ends_at).getTime() - now);
  const m = Math.floor(remMs / 60000);
  const s = Math.floor((remMs % 60000) / 1000);
  return (
    <div className="panel-inset p-3 flex items-center gap-3" data-testid="wild-bounty">
      <div className="w-9 h-9 rounded border border-amber-700 bg-amber-950/40 flex items-center justify-center shrink-0">
        <Target size={16} className="text-amber-300" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-numeric text-amber-400 mb-0.5">
          Standing Bounty
        </div>
        <div className="font-body text-sm text-stone-200 truncate">
          Fell <span className="text-amber-300">{bounty.target_name}</span> to claim
          <span className="text-amber-300"> {bounty.gold.toLocaleString()} gp</span>
        </div>
      </div>
      <span className="text-[11px] font-numeric text-amber-300 flex items-center gap-1">
        <Clock size={11} /> {m}:{s.toString().padStart(2, "0")}
      </span>
    </div>
  );
}

export function WildernessPanel() {
  const { snap, refresh, content } = useGame();
  const openPlayer = useOpenPlayer();
  const [state, setState] = useState(null);
  const [regions, setRegions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);   // {damage,fatal,gold_taken,items_taken}

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        api.get("/api/game/wilderness/state"),
        api.get("/api/game/wilderness/regions"),
      ]);
      setState(s.data);
      setRegions(r.data.regions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const inWild = state?.wilderness_state?.in_wilderness;
    const iv = setInterval(load, inWild ? 1500 : 5000);
    return () => clearInterval(iv);
  }, [load, state?.wilderness_state?.in_wilderness]);

  const inWild = state?.wilderness_state?.in_wilderness;
  const ws     = state?.wilderness_state || {};
  const cfg    = state?.config || {};
  const bounty = state?.bounty;
  const targets = state?.targets || [];
  const myId   = state?.me?.id;

  const enter = async (region_id) => {
    setBusy(true);
    try {
      await api.post("/api/game/wilderness/enter", { region_id });
      toast.success("You cross into the Wilds. All rules of law fall away.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true);
    try {
      await api.post("/api/game/wilderness/leave");
      toast.success("You slip back into civilised territory.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const attack = async (target_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/game/wilderness/attack", { target_id });
      setLastResult(data);
      if (data.fatal) {
        toast.success(`Killing blow! Looted ${data.gold_taken.toLocaleString()} gp` +
          (data.bounty_gold ? ` (+${data.bounty_gold.toLocaleString()} bounty)` : "")
        );
        refresh?.();  // refresh main snap so gold updates
      } else if (data.damage > 0) {
        toast.message(`Strike hit for ${data.damage}`);
      } else {
        toast.message("Your blow glances off — no damage.");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Detect self death: had state, now state cleared, and last kill row matches me as victim
  const iJustDied = useMemo(() => {
    if (inWild) return false;
    const k = (state?.kills || [])[0];
    if (!k || k.victim_id !== myId) return false;
    // Only show within 15s of the kill
    return (Date.now() - new Date(k.at).getTime()) < 15_000;
  }, [inWild, state?.kills, myId]);

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="wilderness-panel">
      {/* Header */}
      <div className="panel overflow-hidden">
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg border-2 border-red-800 bg-stone-950 flex items-center justify-center">
            <Flame size={30} className="text-red-500" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl heading-gilded">The Wilds</h1>
            <p className="text-stone-400 font-body italic text-xs sm:text-sm">
              Where a country's writ ends, another's steel begins. Enter unwilling, leave unwhole.
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs sm:text-sm font-numeric text-stone-300">
              <span>Range · <span className="text-amber-300">±{cfg.cl_range ?? 10} CL</span></span>
              <span>Kills · <span className="text-emerald-400">{state?.totals?.kills ?? 0}</span></span>
              <span>Deaths · <span className="text-red-400">{state?.totals?.deaths ?? 0}</span></span>
              {inWild && (
                <span className="text-red-400 flex items-center gap-1">
                  <ShieldAlert size={12} /> PvP flagged
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Death splash */}
      {iJustDied && (
        <div className="panel p-6 border border-red-900 bg-red-950/20 text-center" data-testid="wild-death-splash">
          <Skull size={32} className="text-red-500 mx-auto mb-2" />
          <div className="font-display text-2xl heading-gilded text-red-300">You have fallen.</div>
          <div className="text-stone-400 text-sm mt-1">
            You respawn in your homeland. Some gold and gear were taken by your killer.
          </div>
        </div>
      )}

      {/* In-region view */}
      {inWild ? (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div className="space-y-4">
            {/* Region + HP */}
            <div className="panel p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-numeric text-red-400 flex items-center gap-1">
                    <MapPin size={11} /> You stand in
                  </div>
                  <div className="font-display text-xl heading-gilded truncate">{ws.region_name}</div>
                  <div className="text-[11px] font-numeric text-stone-500">
                    Held by <span className="text-stone-300">{ws.holder_name || "—"}</span>
                    <span className="text-stone-700"> · </span>
                    /{ws.region_id}/
                  </div>
                </div>
                <button
                  onClick={leave}
                  disabled={busy}
                  className="text-xs font-body px-3 py-1.5 rounded border border-stone-700 text-stone-300 hover:text-red-300 hover:border-red-800 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="wild-leave"
                >
                  <LogOut size={12} /> Leave the Wilds
                </button>
              </div>
              <HpBar hp={ws.current_hp ?? 0} max={ws.max_hp ?? 0} />
            </div>

            {/* Bounty */}
            {bounty && <BountyCard bounty={bounty} />}

            {/* Targets */}
            <div className="panel p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 flex items-center gap-1">
                  <Swords size={12} /> In sight (±{cfg.cl_range ?? 10} CL)
                </h3>
                <span className="text-[10px] font-numeric text-stone-500">{targets.length} found</span>
              </div>
              {targets.length === 0 ? (
                <div className="text-stone-500 italic font-body text-sm py-6 text-center">
                  Nothing but wind. Wait for a challenger — or hunt in a busier region.
                </div>
              ) : (
                <div className="space-y-2">
                  {targets.map((t) => (
                    <TargetRow
                      key={t.id}
                      target={t}
                      onAttack={attack}
                      busy={busy}
                      isBounty={bounty && bounty.target_id === t.id}
                      onOpenProfile={() => openPlayer(t.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Last strike result */}
            {lastResult && !lastResult.fatal && (
              <div className="panel-inset p-3 text-xs font-body text-stone-300" data-testid="wild-last-hit">
                {lastResult.damage > 0
                  ? <>Last blow landed for <span className="text-amber-300 font-numeric">{lastResult.damage}</span>. Opponent at <span className="text-amber-300 font-numeric">{lastResult.target_hp}</span>/<span className="font-numeric">{lastResult.target_max_hp}</span> HP.</>
                  : <>Your last strike glanced off their guard.</>
                }
              </div>
            )}
          </div>

          {/* Kill feed sidebar */}
          <div className="panel p-4">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2 flex items-center gap-1">
              <Trophy size={12} /> Recent kills (this region)
            </h3>
            {(state?.kills || []).length === 0 ? (
              <div className="text-stone-500 italic font-body text-sm py-6 text-center">
                No blood spilled here — yet.
              </div>
            ) : (
              <div className="space-y-1">
                {(state.kills || []).map((k) => (
                  <KillFeedRow key={k.id} k={k} meId={myId} />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Browse mode */
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
          <div className="panel p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="uppercase tracking-widest text-[10px] font-numeric text-red-400 flex items-center gap-1">
                <Flame size={12} /> Flagged Wilderness regions
              </h3>
              <span className="text-[10px] font-numeric text-stone-500">{regions.length} across the realm</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <StatChip icon={Heart}  label="Your HP"          tone="green" value={state?.me ? Math.max(10, (state.me.combat_level || 1))  : "—"} testid="wild-stat-hp" />
              <StatChip icon={Swords} label="Combat Level"      tone="amber" value={state?.me?.combat_level ?? "—"} testid="wild-stat-cl" />
              <StatChip icon={Coins}  label="Carried gp (10% risk)" tone="amber" value={(state?.me?.gold_carried ?? 0).toLocaleString()} testid="wild-stat-gold" />
              <StatChip icon={Skull}  label="Total deaths"     tone="red"   value={state?.totals?.deaths ?? 0} testid="wild-stat-deaths" />
            </div>
            {regions.length === 0 ? (
              <div className="text-stone-500 italic font-body text-sm py-8 text-center border-t border-stone-800/60">
                No region is currently open for blood. Ask a Country Leader to flag one from the Country panel.
              </div>
            ) : (
              <div className="space-y-2">
                {regions.map((r) => (
                  <RegionRow key={r.region_id} region={r} onEnter={enter} busy={busy} />
                ))}
              </div>
            )}
          </div>

          {/* Global kill feed */}
          <div className="panel p-4">
            <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2 flex items-center gap-1">
              <Trophy size={12} /> Recent kills across the Wilds
            </h3>
            {(state?.kills || []).length === 0 ? (
              <div className="text-stone-500 italic font-body text-sm py-6 text-center">
                No blood spilled yet — be the first legend.
              </div>
            ) : (
              <div className="space-y-1">
                {(state.kills || []).map((k) => (
                  <KillFeedRow key={k.id} k={k} meId={myId} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loot list when fatal blow just occurred */}
      {lastResult?.fatal && (lastResult.items_taken?.length > 0 || lastResult.gold_taken > 0) && (
        <div className="panel p-4 border border-amber-800/40 bg-amber-950/10" data-testid="wild-loot-splash">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-400 mb-2 flex items-center gap-1">
            <Trophy size={12} /> Spoils of the kill
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {lastResult.gold_taken > 0 && (
              <span className="text-xs font-numeric text-amber-300 bg-amber-950/40 border border-amber-800 rounded px-2 py-1">
                +{lastResult.gold_taken.toLocaleString()} gp
              </span>
            )}
            {(lastResult.bounty_gold || 0) > 0 && (
              <span className="text-xs font-numeric text-amber-200 bg-amber-950/60 border border-amber-700 rounded px-2 py-1">
                +{lastResult.bounty_gold.toLocaleString()} gp bounty
              </span>
            )}
            {(lastResult.items_taken || []).map((it, idx) => (
              <span key={idx} className="text-xs font-body text-stone-200 bg-stone-900 border border-stone-700 rounded px-2 py-1 flex items-center gap-1.5">
                <span className="text-base">{ITEM_ICON[it.item] || "◇"}</span>
                {itemName(it.item, content?.items)} <span className="text-stone-500 font-numeric">×{it.qty}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
